import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  VERIFICATION_STATUSES,
  type ChecklistItem,
  type Verification,
  type VerificationStats,
  type VerificationStatus,
  type VerificationType,
  type VerificationsParams,
  type VerificationsResponse,
} from '@/client/types/verifications';

export const VERIFICATIONS_BASE = '/api/admin/verifications';

export const verificationKeys = {
  all: ['verifications-admin'] as const,
  lists: () => [...verificationKeys.all, 'list'] as const,
  list: (params: VerificationsParams) => [...verificationKeys.lists(), params] as const,
  detail: (vid: number) => [...verificationKeys.all, 'detail', vid] as const,
  types: () => [...verificationKeys.all, 'types'] as const,
};

const EMPTY_COUNTS = Object.fromEntries(VERIFICATION_STATUSES.map((s) => [s, 0])) as Record<
  VerificationStatus,
  number
>;

// Admin routes answer with bare `jsonify`; tolerate a `{status, data}` envelope too.
const unwrap = (raw: unknown): any =>
  raw != null && typeof raw === 'object' && 'data' in (raw as object) && 'status' in (raw as object)
    ? (raw as { data: unknown }).data
    : raw;

const normalizeList = (raw: unknown): VerificationsResponse => {
  const payload = unwrap(raw) ?? {};
  const verifications: Verification[] = Array.isArray(payload.verifications) ? payload.verifications : [];
  const paging = payload.pagination ?? payload;
  return {
    verifications,
    total: Number(paging.total ?? verifications.length) || 0,
    page: Number(paging.page ?? 1) || 1,
    page_size: Number(paging.page_size ?? 25) || 25,
    counts: { ...EMPTY_COUNTS, ...(payload.counts ?? {}) },
  };
};

const toQuery = (params: VerificationsParams): string => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

export const useVerificationsQuery = (params: VerificationsParams) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: verificationKeys.list(params),
    queryFn: withErrorHandling(
      async () => normalizeList(await request<unknown>(`${VERIFICATIONS_BASE}${toQuery(params)}`)),
      'Could not load verifications',
    ),
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Populates the type filter. Types change rarely, so this is cached longer. */
export const useVerificationTypesQuery = () => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: verificationKeys.types(),
    queryFn: withErrorHandling(async () => {
      const payload = unwrap(await request<unknown>(`${VERIFICATIONS_BASE}/types`)) ?? {};
      return (Array.isArray(payload.types) ? payload.types : []) as VerificationType[];
    }, 'Could not load verification types'),
    staleTime: DEFAULT_STALE_TIME * 10,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useVerificationChecklistQuery = (vid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...verificationKeys.detail(vid ?? 0), 'checklist'],
    queryFn: withErrorHandling(async () => {
      const payload = unwrap(await request<unknown>(`${VERIFICATIONS_BASE}/${vid}/checklist`)) ?? {};
      return (Array.isArray(payload.items) ? payload.items : []) as ChecklistItem[];
    }, 'Could not load the checklist'),
    enabled: Boolean(vid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Queue health: the daily series behind the tiles and the per-type breakdown. */
export const useVerificationStatsQuery = (days = 30) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...verificationKeys.all, 'stats', days],
    queryFn: withErrorHandling(async () => {
      const payload = unwrap(await request<unknown>(`${VERIFICATIONS_BASE}/stats?days=${days}`)) ?? {};
      return {
        days: Number(payload.days ?? days),
        series: Array.isArray(payload.series) ? payload.series : [],
        by_type: Array.isArray(payload.by_type) ? payload.by_type : [],
        oldest_waiting_hours:
          payload.oldest_waiting_hours == null ? null : Number(payload.oldest_waiting_hours),
      } as VerificationStats;
    }, 'Could not load verification stats'),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Everything previously attempted for the same subject. */
export const useSubjectHistoryQuery = (subject: { kind: string | null; id: number | null } | null) => {
  const request = useClientStore((s) => s.request);
  const param = subject?.kind === 'organization' ? 'oid' : subject?.kind === 'listing' ? 'eid' : 'uid';
  return useQuery({
    queryKey: [...verificationKeys.all, 'subject', param, subject?.id ?? 0],
    queryFn: withErrorHandling(async () => {
      const payload = unwrap(await request<unknown>(`${VERIFICATIONS_BASE}/subject?${param}=${subject?.id}`)) ?? {};
      return (Array.isArray(payload.verifications) ? payload.verifications : []) as Verification[];
    }, 'Could not load the subject history'),
    enabled: Boolean(subject?.id && subject?.kind),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
